import type { CalendarEvent } from "../types";

// Keep the existing 24-hour, ten-minute grid and floor-to-block semantics.
export const BLOCKS_PER_HOUR = 6;
export const BLOCKS_PER_DAY = 144;

export const TIME_BLOCKS = Array.from({ length: BLOCKS_PER_DAY }, (_, index) => {
  const hour = Math.floor(index / 6);
  const minute = (index % 6) * 10;
  return { index, hour, minute, timeString: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}` };
});

export const getTimeBlockIndex = (time: string): number => {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 6 + Math.floor(minute / 10);
};

export const indexEventsByBlock = (events: CalendarEvent[]) => {
  const blocks: CalendarEvent[][] = Array.from({ length: 144 }, () => []);
  for (const event of events) {
    const index = getTimeBlockIndex(event.startTime?.toString() || "00:00:00");
    blocks[index]?.push(event);
  }
  return blocks;
};

// "HH:mm:ss" -> "HH:mm" without Moment; same output as moment(time, "HH:mm:ss").format("HH:mm") for valid times.
export const formatClock = (time?: unknown): string => {
  if (!time) return "";
  const [hour = "0", minute = "0"] = String(time).split(":");
  return `${hour.padStart(2, "0")}:${minute.padStart(2, "0").slice(0, 2)}`;
};

// Event geometry in blocks: starts at its floored start block, spans at least one block (same as the legacy grid).
export const getEventBlocks = (event: CalendarEvent) => {
  const start = getTimeBlockIndex(event.startTime?.toString() || "00:00:00");
  const end = getTimeBlockIndex(event.endTime?.toString() || "00:00:00");
  return { start, end, span: Math.max(1, end - start) };
};

export type EventColumn = { columnIndex: number; totalColumns: number };

// Side-by-side layout for every overlapping event (not just identical starts).
// Same algorithm as the previous DayView implementation, with block numbers parsed once per event.
export const computeEventColumns = (events: CalendarEvent[]): Map<number, EventColumn> => {
  const result = new Map<number, EventColumn>();
  if (!events.length) return result;
  const items = events.map(event => ({ event, ...getEventBlocks(event) })).sort((a, b) => a.start - b.start);
  const columnEndBlocks: number[] = [];
  const columnOf = new Map<number, number>();
  for (const item of items) {
    if (item.event.id == null) continue;
    let col = 0;
    while (col < columnEndBlocks.length && columnEndBlocks[col] > item.start) col++;
    if (col === columnEndBlocks.length) columnEndBlocks.push(0);
    columnEndBlocks[col] = item.end;
    columnOf.set(item.event.id, col);
  }
  for (const item of items) {
    const id = item.event.id;
    if (id == null) continue;
    const columnIndex = columnOf.get(id) ?? 0;
    let totalColumns = columnIndex + 1;
    for (const other of items) {
      if (other.event.id == null || other.event.id === id) continue;
      if (item.start < other.end && other.start < item.end) totalColumns = Math.max(totalColumns, (columnOf.get(other.event.id) ?? 0) + 1);
    }
    result.set(id, { columnIndex, totalColumns });
  }
  return result;
};

// Merge the 24 per-hour shift flags into contiguous off-shift ranges [startHour, endHour).
export const getOffShiftRanges = (shiftsByHour: boolean[]): Array<[number, number]> => {
  const ranges: Array<[number, number]> = [];
  let start = -1;
  for (let hour = 0; hour <= 24; hour++) {
    const off = hour < 24 && !shiftsByHour[hour];
    if (off && start < 0) start = hour;
    if (!off && start >= 0) { ranges.push([start, hour]); start = -1; }
  }
  return ranges;
};
